/**
 * Authorization Types
 *
 * Core type definitions for the authorization system
 */

/**
 * Actions that can be performed on resources
 */
export enum Action {
  Create = "create",
  Read = "read",
  Update = "update",
  Delete = "delete",
  Approve = "approve",
  Manage = "manage", // Special action that grants all permissions
  Execute = "execute", // Execute operations like processing payments, transfers
  Report = "report", // Generate reports and analytics
  Tag = "tag", // Apply/remove policy & descriptive tags on a resource (ABAC)
}

/**
 * Subject types that can have permissions
 *
 * Organized by functional area for clarity.
 */
export enum Subject {
  // Special
  All = "all", // Reserved for Gently staff bypass only

  // Finance & Accounting
  APInvoice = "APInvoice",
  APInvoicePayment = "APInvoicePayment",
  ARInvoice = "ARInvoice",
  ARPayment = "ARPayment",
  GeneralLedger = "GeneralLedger",
  Account = "Account",
  Budget = "Budget",
  FixedAsset = "FixedAsset",

  // Sales & CRM
  SalesOrder = "SalesOrder",
  SalesOrderDraft = "SalesOrderDraft",
  SalesOrderDraftV3 = "SalesOrderDraftV3",
  // Dedicated capability: reassign a draft sales order to another organization
  // (gated independently of the SalesOrderDraftV3 read/update/approve grants).
  SalesOrderDraftReassign = "SalesOrderDraftReassign",
  Discount = "Discount",
  // Discounts v2 (OMS v3): discount groups + elements + draft-line stacks.
  // Gated separately from the legacy v1 Discount subject so orgs can roll the
  // v2 surface out independently.
  DiscountV2 = "DiscountV2",
  ProductMargins = "ProductMargins",
  ProductCatalog = "ProductCatalog",
  Customer = "Customer",
  Contact = "Contact",
  // Customer SKU → our-product mappings: the Famous EDI translation list
  // (`famous_edi_translation_list_items`) and its legacy `ar_customer_products`
  // representation. Gated separately from Customer so refreshing a stale
  // mapping doesn't require the ability to edit customer master data.
  EdiTranslationList = "EdiTranslationList",
  ServiceTicket = "ServiceTicket",
  Payment = "Payment",

  // Purchasing
  PurchaseOrder = "PurchaseOrder",
  Supplier = "Supplier",
  ExternalPriceSheet = "ExternalPriceSheet",

  // Pricing
  Pricing = "Pricing",
  // Price list master data: synced ERP price lists and manually uploaded
  // exception-price-list overlays. Gated separately from Customer so managing
  // org-wide pricing requires its own grant.
  PriceList = "PriceList",

  // Inventory
  Product = "Product",
  Inventory = "Inventory",
  Warehouse = "Warehouse",

  // Rebates
  RebateConfig = "RebateConfig",
  RebateAgreement = "RebateAgreement",
  RebateDataUpload = "RebateDataUpload",

  // Ship & Debit
  SDTransaction = "SDTransaction",
  VDM = "VDM",
  SD849Response = "SD849Response",
  SDActivityLog = "SDActivityLog",
  SDAdmin = "SDAdmin",
  SDPosting = "SDPosting",
  SDTransactionV2 = "SDTransactionV2",

  // Organization & Administration
  Company = "Company",
  User = "User",
  UserGroup = "UserGroup",
  OrganizationConfig = "OrganizationConfig",
  Division = "Division",
  Integration = "Integration",
  MemoryBankNote = "MemoryBankNote",
  BackgroundAgent = "BackgroundAgent",
  Task = "Task",
  ApiKey = "ApiKey",
  WorkflowApiToken = "WorkflowApiToken",
  GuardrailPolicy = "GuardrailPolicy",
  ApprovalConfig = "ApprovalConfig",
  OutOfOffice = "OutOfOffice",
  ResourcePrivilege = "ResourcePrivilege",
  // Applying/removing tags on a resource is itself an access-control
  // decision (a policy tag can grant or revoke visibility), so it is gated
  // separately from ResourcePrivilege (which governs privilege/scope/tag
  // *definition* administration). See `docs/resource-scope-policy/abac_architecture.md`.
  ResourceTag = "ResourceTag",

  // Communications
  SharedMailboxSend = "SharedMailboxSend",
}

/**
 * Type representing a specific permission
 */
export interface Permission {
  action: Action;
  subject: Subject;
}
